import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Avatar,
  Typography,
  Grid
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { updateProfileAsync, clearError } from "../../../store/slices/authSlice";

/**
 * Componente de perfil do usuário com upload de avatar.
 * Permite atualização de dados pessoais e endereço.
 * Suporta upload de imagem de avatar (PNG, JPG, GIF) com conversão para base64.
 * Modal fecha automaticamente após sucesso, permanece aberto em caso de erro.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
function UserProfile({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { user, isLoading, error } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    logradouro: '',
    numero: '',
    complemento: '',
    cep: '',
    cidade: '',
    estado: '',
    avatar: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    if (user && open) {
      const avatarData = user.avatar 
        ? (user.avatar.startsWith('data:') ? user.avatar : `data:image/png;base64,${user.avatar}`)
        : null;
      setFormData({
        nome: user.nome || '',
        email: user.email || '',
        logradouro: user.logradouro || '',
        numero: user.numero || '',
        complemento: user.complemento || '',
        cep: user.cep || '',
        cidade: user.cidade || '',
        estado: user.estado || '',
        avatar: user.avatar || ''
      });
      setAvatarPreview(avatarData);
      dispatch(clearError());
      setSuccess(false);
    }
  }, [user, open, dispatch]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome) newErrors.nome = 'Nome é obrigatório';
    if (formData.cep && !/^\d{5}-?\d{3}$/.test(formData.cep)) newErrors.cep = 'CEP inválido';
    if (formData.estado && formData.estado.length !== 2) newErrors.estado = 'Estado deve ter 2 caracteres';
    return newErrors;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Formatação automática do CEP
    if (name === 'cep') {
      value = value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    // Formatação automática do estado (maiúsculo)
    if (name === 'estado') {
      value = value.toUpperCase();
    }
    
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (success) setSuccess(false);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setErrors({ ...errors, avatar: 'Apenas imagens PNG, JPG ou GIF são permitidas' });
      return;
    }

    // Validar tamanho (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ ...errors, avatar: 'Imagem deve ter no máximo 2MB' });
      return;
    }

    // Converter para base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      const base64Data = base64String.split(',')[1];
      setFormData({ ...formData, avatar: base64Data });
      setAvatarPreview(base64String);
      if (errors.avatar) {
        setErrors({ ...errors, avatar: '' });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    const result = await dispatch(updateProfileAsync(formData));
    if (updateProfileAsync.fulfilled.match(result)) {
      setSuccess(true);
      // Fechar modal após sucesso
      setTimeout(() => {
        setSuccess(false);
        handleClose();
      }, 1500);
    }
    // Se falhar, mantém modal aberto e erro é exibido automaticamente
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrors({});
      setSuccess(false);
      setAvatarPreview(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Meu Perfil
        <IconButton onClick={handleClose} size="small">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar 
              src={avatarPreview} 
              sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}
            >
              {!avatarPreview && <PersonOutlinedIcon sx={{ fontSize: 40 }} />}
            </Avatar>
            <IconButton
              component="label"
              sx={{
                position: 'absolute',
                bottom: 16,
                right: -8,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'background.paper' }
              }}
              size="small"
            >
              <PhotoCameraIcon fontSize="small" />
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg,image/jpg,image/gif"
                onChange={handleAvatarChange}
              />
            </IconButton>
          </Box>
          {errors.avatar && (
            <Typography variant="caption" color="error" sx={{ mb: 1 }}>
              {errors.avatar}
            </Typography>
          )}
          <Typography variant="h6">{user?.nome}</Typography>
          <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Perfil atualizado com sucesso!
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="nome"
                label="Nome Completo"
                fullWidth
                margin="normal"
                value={formData.nome}
                onChange={handleChange}
                error={!!errors.nome}
                helperText={errors.nome}
                disabled={isLoading}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={formData.email}
                InputProps={{ readOnly: true }}
                disabled
                sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="logradouro"
                label="Logradouro"
                fullWidth
                margin="normal"
                value={formData.logradouro}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="numero"
                label="Número"
                fullWidth
                margin="normal"
                value={formData.numero}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                name="complemento"
                label="Complemento"
                fullWidth
                margin="normal"
                value={formData.complemento}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="cep"
                label="CEP"
                fullWidth
                margin="normal"
                value={formData.cep}
                onChange={handleChange}
                error={!!errors.cep}
                helperText={errors.cep}
                disabled={isLoading}
                placeholder="00000-000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="cidade"
                label="Cidade"
                fullWidth
                margin="normal"
                value={formData.cidade}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                name="estado"
                label="Estado"
                fullWidth
                margin="normal"
                value={formData.estado}
                onChange={handleChange}
                error={!!errors.estado}
                helperText={errors.estado}
                disabled={isLoading}
                placeholder="SP"
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button onClick={handleClose} variant="outlined" fullWidth disabled={isLoading}>
              Cancelar
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default UserProfile;